"use client"
import React, {useEffect, useRef, useState} from "react";
import PageHeaderElement from "./components/page_header";
import {CaosCommand, GameVariant, Nullable, PopupItem, ValuesList} from "./caos-def"
import CommandsElement from "./components/commands";
import ValuesListsElement from "./components/values_lists";
import {getCaosLib} from "./utils.caosdef";
import {CommandFilters, ItemFilter} from "./commands.filters";
import {allVariants, SetVariantFunc} from "./utils.variants";
import init from "./init";
import {
    getInitialQuery,
    getInitialVariantFromUrl,
    getUseSeaMonkeysFromUrl,
    setQueryInUrl,
    setSeaMonkeysInUrl,
    setVariantInURL
} from "./navigation";
import {localStorageGetBooleanNotNull} from "./utils";
import Command from "./components/command";
import ValuesListElement from "./components/values_list";
import PopupElement from "./components/popup_element";


let runOnce = false;

let _lastQuery: Nullable<string> = null;

let queryFilter: Nullable<ItemFilter> = null;

let variantFilter: Nullable<ItemFilter> = null;


const caosLib = getCaosLib();

const allCommands = caosLib.getCommands();

const allValuesLists = caosLib.getValuesLists();

export default function App() {

    // const [commands] = useState<CaosCommand[]>(caosLib.getCommands())
    const [validCommandIds, setValidCommandIds] = useState<Nullable<number[]>>(null)

    // const [valuesLists] = useState<ValuesList[]>(allValuesLists);
    const [validValuesListIds, setValidValuesListIds] = useState<Nullable<number[]>>(null)

    const [_currentVariant, _setVariant] = useState<Nullable<GameVariant>>()

    const [useSeaMonkeys, _setUseSeaMonkeys] = useState<boolean>(getUseSeaMonkeysFromUrl() === true)

    const [_query, _setQuery] = useState<Nullable<string>>(null);

    const [_searchDescriptions, _setSearchDescriptions] = useState<boolean>(
        localStorageGetBooleanNotNull("search-descriptions", CommandFilters.SEARCH_DESCRIPTIONS_DEFAULT)
    );

    const [_popupElements, _setPopupElements] = useState<PopupItem[]>([]);


    const FILTER_DELAY = 500;
    const filterTimeoutHandle: React.MutableRefObject<Nullable<number> | undefined> = useRef<Nullable<number>>();
    const hashChangeTimeoutHandle: React.MutableRefObject<Nullable<number> | undefined> = useRef<Nullable<number>>();


    const setVariant: SetVariantFunc = (newVariant: Nullable<GameVariant>, unset: boolean) => {
        if (newVariant) {
            newVariant = newVariant.toUpperCase() as GameVariant;
        }
        const changed = _currentVariant !== newVariant;

        if (!changed) {
            if (unset) {
                newVariant = null;
            }
        }
        setVariantInURL(newVariant);
        _setVariant(newVariant);

        variantFilter = CommandFilters.getVariantsFilter(caosLib, newVariant);
        runFilter();
    }

    const getVariant = (): Nullable<GameVariant> => _currentVariant;

    function runFilter() {
        if (filterTimeoutHandle.current) {
            clearTimeout(filterTimeoutHandle.current);
        }
        filterTimeoutHandle.current = window.setTimeout(_runFilter, FILTER_DELAY);
    }



    function _runFilter() {
        setValidCommandIds(filterForValidIds(allCommands));
        setValidValuesListIds(filterForValidIds(allValuesLists));
    }


    function pushPopupItem(kind: string, id: number) {
        const elements = [..._popupElements];
        elements.push({kind, id})
        _setPopupElements(elements);
    }

    function popPopupElement(popupItem: PopupItem) {
        if (_popupElements.length > 0) {
            return;
        }
        const elements = [..._popupElements].reverse();
        while (
            elements.length > 0 && (elements[0].kind != popupItem.kind || elements[0].id != popupItem.id)
        ) {
            elements.pop();
        }
        elements.reverse()
        _setPopupElements(elements);
    }


    function filterForValidIds<T extends CaosCommand | ValuesList>(values: T[]): number[] {
        if (variantFilter) {
            values = values.filter(variantFilter);
        }
        if (queryFilter) {
            values = values.filter(queryFilter);
        }
        return values.map(item => item.id);
    }

    function setQuery(query: Nullable<string>, searchDescriptions?: Nullable<boolean>) {
        if (query === _lastQuery  && searchDescriptions === _searchDescriptions) {
            return;
        }

        _lastQuery = query

        if (query == null || query.trim().length == 0) {
            queryFilter = null;
            document.body.setAttribute("data-searching", "false");
        } else {
            queryFilter = CommandFilters.createSearchFilter(query, searchDescriptions ?? _searchDescriptions);
            document.body.setAttribute("data-searching", "true");
        }

        if (searchDescriptions != null && _searchDescriptions !== searchDescriptions) {
            _setSearchDescriptions(searchDescriptions);
            localStorage.setItem("search-descriptions", "" + searchDescriptions);
        }

        setQueryInUrl(query);
        _setQuery(query);
        runFilter();
    }

    function setUseSeaMonkeys(enable: Nullable<boolean>): void {
        const enableNonNull = enable == null || enable;
        if (enableNonNull === useSeaMonkeys) {
            return;
        }

        _setUseSeaMonkeys(enable == true);
        setSeaMonkeysInUrl(enable);
    }

    async function getUrl() {
        return new Promise<string>((resolve: (value: string) => void) => {
            setTimeout(() => resolve(window.location.href), 10);
        });
    }

    function createPopupItem(item: PopupItem, i: number): Nullable<React.JSX.Element> {
        let kind = item.kind?.toLowerCase();
        if (kind == null || kind.length < 1) {
            console.error("Invalid popup element kind is blank");
            return null;
        }
        kind = kind[0];

        const {id} = item;

        if (kind !== "c" && kind !== "l") {
            console.error("Invalid popup element kind: <"  + item.kind + ">");
            return null
        }
        const key = `popup-element-${i}-${kind === "c" ? "command" : "values-list"}-${id}`;
        let element: Nullable<React.JSX.Element> = null;
        if (kind === "c") {
            const command: Nullable<CaosCommand> = allCommands.find(c => c.id == id);
            if (command != null) {
                element = <Command
                    command={command}
                    setVariant={setVariant}
                    caosLib={caosLib}
                    idKey={key + "-child"}
                    useSeaMonkeys={useSeaMonkeys}
                    pushPopupItem={pushPopupItem}
                    show={true}
                />
            }
        } else {
            const valuesList: Nullable<ValuesList> = allValuesLists.find(list => list.id === id);
            if (valuesList != null) {
                element = <ValuesListElement
                    caosLib={caosLib}
                    list={valuesList}
                    setVariant={setVariant}
                    show={true}
                    useSeaMonkeys={useSeaMonkeys}
                    pushPopupItem={pushPopupItem}
                />;
            }
        }
        if (!element) {
            return null
        }
        return <PopupElement
            key={key}
            popupElement={item}
            popPopupElement={popPopupElement}
            index={i}
        >{element}</PopupElement>;
    }


    init(document, getVariant, setVariant, setUseSeaMonkeys);

    function updateFromHash() {
        setVariant(getInitialVariantFromUrl(), false);
        setVariantInBodyAttributes(_currentVariant);
        setQuery(getInitialQuery(), _searchDescriptions);
    }

    function onHashChange() {
        if (hashChangeTimeoutHandle.current) {
            clearTimeout(hashChangeTimeoutHandle.current);
        }
        hashChangeTimeoutHandle.current = window.setTimeout(updateFromHash, 200);
    }

    useEffect(() => {
        window.addEventListener("hashchange", onHashChange);
        return () => {
            window.removeEventListener("hashchange", onHashChange);
        };
    }, []);

    if (!runOnce) {
        runOnce = true;
        onHashChange();
        return;
    }

    let popups: React.JSX.Element[] = [];
    if (_popupElements && _popupElements.length > 0) {
        document.body.setAttribute("data-popups", "true")
        popups = _popupElements
            .map(createPopupItem)
            .filter(item => item != null) as React.JSX.Element[];
    } else {
        document.body.setAttribute("data-popups", "false")
    }

    return <>
        {popups}
        <PageHeaderElement variant={_currentVariant}
                           setVariant={setVariant}
                           setQuery={setQuery}
                           query={_query}
                           useSeaMonkeys={useSeaMonkeys}
                           getUrl={getUrl}
                           searchDescriptions={_searchDescriptions}
        />
        <main>
            <div className="scroll-panel">
                <CommandsElement caosLib={caosLib}
                                 commands={allCommands}
                                 setVariant={setVariant}
                                 useSeaMonkeys={useSeaMonkeys}
                                 pushPopupItem={pushPopupItem}
                                 validCommandIds={validCommandIds}
                />
                <ValuesListsElement caosLib={caosLib}
                                    setVariant={setVariant}
                                    valuesLists={allValuesLists}
                                    useSeaMonkeys={useSeaMonkeys}
                                    pushPopupItem={pushPopupItem}
                                    validValuesListsIds={validValuesListIds}
                />
            </div>
        </main>
    </>
}


function setVariantInBodyAttributes(newVariant: Nullable<GameVariant>) {
    const body = document.body;

    for (const variant of allVariants) {
        body.setAttribute("data-variant-" + variant, "" + (variant === newVariant));
    }

}